import {Component, OnInit, HostListener} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {MenuItem, MessageService} from 'primeng/api';
import {NavigationService} from '../../../../service/navigation.service';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {AccountService} from '../../../../service/account.service';
import {AccountInterface} from '../../../../model/account.interface';
import {IpopConfigurationService} from '../../../../service/ipop-configuration.service';
import {IpopConfiguration} from '../../../../model/ipop-configuration.model';

@Component({
    selector: 'auto-ipop-configuration',
    templateUrl: './ipop-configuration.component.html',
    styleUrls: ['./ipop-configuration.component.scss'],
    standalone: false
})
export class IpopConfigurationComponent implements OnInit {
  accountId: number;
  settingsSideNav: MenuItem[];
  account: AccountInterface;
  privateOffersForm: UntypedFormGroup;
  vdpOffersForm: UntypedFormGroup;
  ipopConfiguration: IpopConfiguration | null = null;
  isSaving = false;
  activeTab = 'privateOffers';

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    protected formBuilder: UntypedFormBuilder,
    private navigationService: NavigationService,
    protected messageService: MessageService,
    protected accountService: AccountService,
    private ipopConfigurationService: IpopConfigurationService,
  ) {
  }

  async ngOnInit(): Promise<void> {
    this.titleService.setTitle('IPOP');
    this.route.paramMap.subscribe((params) => {
      const idParam = params.get('accountId');
      if (idParam) {
        this.accountId = parseInt(idParam);
        this.initNavigation();
        this.loadSettings();
      }
    });

    this.route.queryParamMap.subscribe((queryParams) => {
      const requestedTab = queryParams.get('tab');

      if (requestedTab === 'offerSetRules') {
        this.activeTab = 'offerSetRules';

        return;
      }

      if (requestedTab === 'vdpOffers') {
        this.activeTab = 'vdpOffers';

        return;
      }

      this.activeTab = 'privateOffers';
    });
  }

  /**
   * Initialize the side navigation menu
   */
  initNavigation(): void {
    this.navigationService
      .getMenuItems(this.accountId, 'settings-view-side-menu.configuration.json')
      .then((navItems) => (this.settingsSideNav = navItems));
  }

  /**
   * Load account and IPOP configurations
   */
  private loadSettings(): void {
    this.accountService.getAccount(this.accountId).subscribe({
      next: (account) => {
        this.account = account;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load account'
        });
      }
    });

    this.ipopConfigurationService.getSettings(this.accountId).subscribe({
      next: (settings) => {
        this.ipopConfiguration = settings;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load settings'
        });
      }
    });
  }

  /**
   * Initialize the private offers form group
   */
  onPrivateOffersFormGroupInitialized(form: UntypedFormGroup) {
    this.privateOffersForm = form;
  }

  /**
   * Initialize the VDP offers form group
   */
  onVdpOffersFormGroupInitialized(form: UntypedFormGroup) {
    this.vdpOffersForm = form;
  }

  /**
   * Check if the save button should be disabled
   */
  getSaveButtonDisabled(): boolean {
    let isDisabled = true;
    if (this.privateOffersForm) {
      if (this.privateOffersForm.dirty && this.privateOffersForm.valid) {
        isDisabled = false;
      }
    }
    if (this.vdpOffersForm) {
      if (this.vdpOffersForm.dirty && this.vdpOffersForm.valid) {
        isDisabled = false;
      }
    }
    return isDisabled;
  }

  /**
   * Save IPOP configuration
   */
  saveSettings(): void {
    let payload: IpopConfiguration | null = null;

    if (this.ipopConfiguration) {
      this.isSaving = true;
      payload = this.buildSaveSettingsPayload(this.ipopConfiguration);
    }

    if (payload) {
      this.ipopConfigurationService.updateSettings(payload).subscribe({
        next: () => {
          this.handleSaveSuccess();
        },
        error: () => {
          this.handleSaveError();
        },
        complete: () => {
          this.handleSaveComplete();
        }
      });
    }
  }

  /**
   * Build the full IPOP configuration payload from the current forms.
   */
  private buildSaveSettingsPayload(ipopConfiguration: IpopConfiguration): IpopConfiguration {
    const includePrivateOffersInExternalAdvertising = this.resolveBooleanControlValue(
      this.privateOffersForm,
      'includePrivateOffersInExternalAdvertising',
      false
    );
    const unlockPrivateOffersEnabled = this.resolveBooleanControlValue(this.privateOffersForm, 'unlockPrivateOffersEnabled', false);
    const displaySavingsMessage = this.resolveBooleanControlValue(this.privateOffersForm, 'displaySavingsMessage', true);
    const savingsMessage = this.resolveStringControlValue(this.privateOffersForm, 'savingsMessage', 'Instant Savings Unlocked');
    const savingsMessageBackgroundColor = this.resolveStringControlValue(this.privateOffersForm, 'savingsMessageBackgroundColor', '#DDE9E2');
    const showPriceComparison = this.resolveBooleanControlValue(this.privateOffersForm, 'showPriceComparison', false);
    const preUnlockCtaText = this.resolveStringControlValue(this.privateOffersForm, 'preUnlockCtaText', 'Unlock Your Special Offer');
    const postUnlockCtaText = this.resolveStringControlValue(this.privateOffersForm, 'postUnlockCtaText', 'Claim Your Special Offer');
    const privateOfferTextColor = this.resolveStringControlValue(this.privateOffersForm, 'privateOfferTextColor', '#000000');

    const vdpOfferWidgetEnabled = this.resolveBooleanControlValue(this.vdpOffersForm, 'vdpOfferWidgetEnabled', false);
    const offerTextColor = this.resolveStringControlValue(this.vdpOffersForm, 'offerTextColor', '#000000');
    const ctaTextFontSize = this.resolveNumericControlValue(this.vdpOffersForm, 'ctaTextFontSize', 22);
    const ctaButtonColor = this.resolveStringControlValue(this.vdpOffersForm, 'ctaButtonColor', '#000000');
    let privateOfferDisclosure = '';
    if (this.privateOffersForm) {
      const value = this.privateOffersForm.get('privateOfferDisclosure')?.value;
      if (typeof value === 'string') {
        privateOfferDisclosure = value.trim();
      }
    }

    return {
      id: ipopConfiguration.id,
      accountId: this.accountId,
      includePrivateOffersInExternalAdvertising: includePrivateOffersInExternalAdvertising,
      unlockPrivateOffersEnabled: unlockPrivateOffersEnabled,
      vdpOfferWidgetEnabled: vdpOfferWidgetEnabled,
      displaySavingsMessage: displaySavingsMessage,
      savingsMessage: savingsMessage,
      savingsMessageBackgroundColor: savingsMessageBackgroundColor,
      offerTextColor: offerTextColor,
      privateOfferTextColor: privateOfferTextColor,
      showPriceComparison: showPriceComparison,
      preUnlockCtaText: preUnlockCtaText,
      postUnlockCtaText: postUnlockCtaText,
      ctaTextFontSize: ctaTextFontSize,
      ctaButtonColor: ctaButtonColor,
      privateOfferDisclosure: privateOfferDisclosure,
    };
  }

  /**
   * Resolve a boolean form control value with a default when the form/control is missing.
   */
  private resolveBooleanControlValue(form: UntypedFormGroup | null, controlName: string, defaultValue: boolean): boolean {
    let result = defaultValue;
    if (form) {
      const value = form.get(controlName)?.value;
      if (value === true) {
        result = true;
      }
      if (value === false) {
        result = false;
      }
    }
    return result;
  }

  /**
   * Resolve a trimmed string form control value with a default when missing/blank.
   */
  private resolveStringControlValue(form: UntypedFormGroup | null, controlName: string, defaultValue: string): string {
    let result = defaultValue;
    if (form) {
      const value = form.get(controlName)?.value;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed !== '') {
          result = trimmed;
        }
      }
    }
    return result;
  }

  /**
   * Resolve a number form control value with a default when missing/blank.
   */
  private resolveNumericControlValue(form: UntypedFormGroup | null, controlName: string, defaultValue: number): number {
    let result = defaultValue;
    if (form) {
      const value = form.get(controlName)?.value;
      if (typeof value === 'number') {
       result = value;
      }
    }
    return result;
  }

  /**
   * Handle successful saves.
   */
  private handleSaveSuccess(): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Saved',
      detail: 'Settings updated'
    });
    this.markFormsPristine();
  }

  /**
   * Handle failed saves.
   */
  private handleSaveError(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save settings'
    });
    this.isSaving = false;
  }

  /**
   * Handle save completion.
   */
  private handleSaveComplete(): void {
    this.isSaving = false;
  }

  /**
   * Mark forms pristine after persisting settings.
   */
  private markFormsPristine(): void {
    if (this.privateOffersForm) {
      this.privateOffersForm.markAsPristine();
    }
    if (this.vdpOffersForm) {
      this.vdpOffersForm.markAsPristine();
    }
  }

  /**
   * Show native prompt on browser/tab refresh or close
   */
  @HostListener('window:beforeunload', ['$event'])
  confirmOnBrowserClose(event: BeforeUnloadEvent): void {
    let shouldPrompt = false;
    if (this.privateOffersForm) {
      if (this.privateOffersForm.dirty) {
        shouldPrompt = true;
      }
    }
    if (this.vdpOffersForm) {
      if (this.vdpOffersForm.dirty) {
        shouldPrompt = true;
      }
    }
    if (shouldPrompt) {
      event.returnValue = true;
    }
  }
}
